// Track the patched functions so repeated calls (e.g. the dev server re-running the entry module) don't wrap the
// wrapper and duplicate warnings.
const patchedMethods = new WeakSet<object>();

/**
 * Chrome's built-in Google Translate (and similar extensions) mutates DOM that React manages: it wraps every text
 * node in nested `<font>` elements, re-parenting nodes React expects to own. When React later commits an update
 * that removes or repositions one of those nodes, the native methods throw "NotFoundError: Failed to execute
 * 'removeChild' on 'Node'" and the nearest error boundary tears the page down mid-chat. React cannot recover from
 * this itself (https://github.com/facebook/react/issues/11538), so the recommended workaround is to make the two
 * DOM methods tolerate a re-parented node: skip the operation instead of throwing. The worst case is a stale
 * translated string briefly left on screen, which is far better than a crash. Call before React renders; repeated
 * calls are no-ops.
 */
export const patchDomMethods = (): void => {
    if (!patchedMethods.has(Node.prototype.removeChild)) {
        const originalRemoveChild = Node.prototype.removeChild;
        Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
            if (child.parentNode !== this) {
                console.warn("Skipped removeChild on a node whose parent changed (translation extension?)", child);
                return child;
            }
            // `.call` widens the generic to `Node`; the original method returns the same node it was given.
            return originalRemoveChild.call(this, child) as T;
        };
        patchedMethods.add(Node.prototype.removeChild);
    }

    if (!patchedMethods.has(Node.prototype.insertBefore)) {
        const originalInsertBefore = Node.prototype.insertBefore;
        Node.prototype.insertBefore = function <T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
            if (referenceNode !== null && referenceNode.parentNode !== this) {
                console.warn(
                    "Skipped insertBefore with a reference node whose parent changed (translation extension?)",
                    referenceNode
                );
                return newNode;
            }
            // `.call` widens the generic to `Node`; the original method returns the same node it was given.
            return originalInsertBefore.call(this, newNode, referenceNode) as T;
        };
        patchedMethods.add(Node.prototype.insertBefore);
    }
};
